import React, { memo } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { MESSAGE_TYPES } from "../../utils/Enum";
import { botInfoStore } from "../../../Store/Slices/socket/BotInfoSlice";
import AppMessageBoxWrapper from "../UtilsComponents/AppMessageBoxWrapper";
import AppRenderNestedFlowButtons from "./nestedFlow/AppRenderNestedFlowButtons";
import AppRenderNestedText from "./nestedFlow/AppRenderNestedText";
import AppRenderNestedLiveButtons from "./nestedFlow/AppRenderNestedLiveButtons";
import AppRenderNestedCtaButtons from "./nestedFlow/AppRenderNestedCtaButtons";
import { StyledReactVideoPlayer } from "../../Styles/StyledComponents";
import { Box } from "@mui/material";
import styled from "styled-components";

const FallbackImage = require("../../../assets/images/Image_not_available.png");
const BUTTON_TYPES = [
  MESSAGE_TYPES.FLOW,
  MESSAGE_TYPES.LIVE_AGENT_REQUESTED,
  MESSAGE_TYPES.LINK,
  MESSAGE_TYPES.MOBILE,
  MESSAGE_TYPES.EMAIL,
];

interface Props {
  userMessage: boolean;
  item: any;
  rowWrapperStyle?: React.CSSProperties | undefined;
  containerStyle?: React.CSSProperties | undefined;
  time: string;
  readStatus?: "sent" | "delivered" | "seen";
  [otherProps: string]: any;
}

const AppRenderCard: React.FC<Props> = ({
  userMessage,
  item,
  rowWrapperStyle,
  containerStyle,
  time,
  readStatus = "delivered",
  otherProps,
}) => {
  const { isBotSetToFullScreen } = useSelector(botInfoStore);
  const theme: any = useTheme();
  const isNewUi = !!theme?.industry_style?.enabled;
  const buttonCount =
    item?.filter((curItem: any) => BUTTON_TYPES.includes(curItem?.type))
      ?.length ?? 0;
  // Same reasoning as the carousel card's button list: Expand mode's panel
  // is wide enough that stacking one button per row wastes most of it
  // regardless of count, while the docked width only benefits from
  // wrapping once there are enough buttons to make the stack tall. Image/
  // video/text below keep width:100% (ImageWrapper already had it; the
  // text wrapper adds it) so they still claim the whole row and force a
  // new line — only the buttons actually share rows. Gated with the rest
  // of the redesign; the legacy layout keeps its original single column.
  const shouldWrapButtons =
    isNewUi && (isBotSetToFullScreen || buttonCount > 5);

  return (
    <AppMessageBoxWrapper
      userMessage={userMessage}
      time={time}
      isMedia={false}
      isButton={false}
      divStyle={{
        maxWidth: "90%",
        flexDirection: shouldWrapButtons ? "row" : "column",
        flexWrap: shouldWrapButtons ? "wrap" : "nowrap",
        alignItems: "flex-start",
        gap: "12px",
        boxShadow:"none",
        backgroundColor: "transparent",
        borderRadius: "8px",
      }}
    >
      {item?.map((item: any, index: number) => {

        // * IMAGE MESSAGE
        if (item?.type === MESSAGE_TYPES.IMAGE)
          return (
        <ImageWrapper>
            <a key={index.toString()} className="message-image" href={item?.value?.url} target="_blank" rel="noreferrer">
              <img
                className="renderImage"
                src={item?.value?.url}
                alt={item?.value?.alt}
                // onClick={() => window.open(src, "_blank")}
                onError={(error: any) => {
                  error.target.src = FallbackImage;
                }}
              />
            </a>
          </ImageWrapper>
          );


        // Video Message
        if (item?.type === MESSAGE_TYPES.VIDEO)
          return (
            <StyledReactVideoPlayer
              key={index.toString()}
              url={item?.value?.url}
              width={"100%"}
              controls={true}
              light={false}
              alt={item?.value?.url}
              height={180}
            />
          );

        if (item?.type === MESSAGE_TYPES.TEXT)
          return (
            <div key={index.toString()} style={{ width: "100%" }}>
              <AppRenderNestedText>
                {item?.text || item?.value}
              </AppRenderNestedText>
            </div>
          );

        // * FLOW BUTTONS
        if (item?.type === MESSAGE_TYPES.FLOW)
          return (
            <AppRenderNestedFlowButtons
              key={index.toString()}
              userMessage={userMessage}
              item={item}
              time={item?.time}
            />
          );

        // * LIVE CHAT BUTTONS
        if (item?.type === MESSAGE_TYPES.LIVE_AGENT_REQUESTED) {
          return (
            <AppRenderNestedLiveButtons
              key={index.toString()}
              item={item}
              time={item?.time}
            />
          );
        }

        // * CTA BUTTONS
        if (
          item?.type === MESSAGE_TYPES.LINK ||
          item?.type === MESSAGE_TYPES.MOBILE ||
          item?.type === MESSAGE_TYPES.EMAIL
        ) {
          return (
            <AppRenderNestedCtaButtons
              item={item}
              key={index.toString()}
              time={item?.time}
            />
          );
        }

        return null;
      })}
    </AppMessageBoxWrapper>
  );
};

export default memo(AppRenderCard);

const ImageWrapper = styled.div`
&& {
  width: 100%;
  .renderImage {
    width: 100%;
    max-width: 300px;
    max-height: 300px;
    object-fit: contain;
    width: 100%;
    object-fit: contain;
    border-radius: 4px;
  }
}
`;